/* eslint no-use-before-define: 0 */
import pick from 'lodash/pick';
import { replace } from 'connected-react-router';
import moment from 'moment';
import axios from 'axios';
import { defaultScenarios, defaultPreviewSimple } from './constants';

import {
  defaultState,
  RESET,
  FETCH_PRODUCTS_LOADING,
  FETCH_PRODUCTS_ERROR,
  FETCH_PRODUCTS_DATA,
  SET_PRODUCT_ID,
  SET_POLLUTANT,
  SET_SCENARIO,
  SET_COMPARING_SCENARIO,
  SET_STATE,
  SET_YEAR,
  SET_MONTH,
  SET_DAY,
  SET_HOUR,
  SET_MEMBER,
  SET_COMPARING_YEAR,
  SET_COMPARING_MONTH,
  SET_COMPARING_DAY,
  SET_COMPARING_HOUR,
  SET_BASEMAP,
  SET_LABELS,
  SET_LANGUAGE,
  SET_TIMEZONE,
  SET_ZONE,
  SET_VIEWPORT,
  UPDATE_DATE,
  UPDATE_COMPARING_DATE,
  UPDATE_DATE_TIMEZONE,
  UPDATE_COMPARING_DATE_TIMEZONE,
} from './reducer';

import {
  getValidProducts,
  getScenarios,
  getSelectedScenario,
  getComparingScenarios,
  getSelectedComparingScenario,
  getYears,
  getMonths,
  getDays,
  getHours,
  getSelectedMonth,
  getSelectedDay,
  getSelectedHour,
  getAreas,
  getSelectedArea,
  getMembers,
  getSelectedMember,
  getComparingYears,
  getComparingMonths,
  getComparingDays,
  getComparingHours,
  getSelectedProduct,
  getSelectedYear,
  getSelectedComparingMonth,
  getSelectedComparingDay,
  getSelectedComparingHour,
} from './selectors';

/**
 * Reset the store
 */
export const reset = () => ({ type: RESET });

/**
 * Fetch the list of products
 */
export const fetchProducts = () => (dispatch, getState) => {
  dispatch({ type: FETCH_PRODUCTS_LOADING, payload: true });
  dispatch({ type: FETCH_PRODUCTS_ERROR, payload: false });

  axios({
    url: `${process.env.REACT_APP_API_URL}/products?onePerProductLine=true&isServerRenderable=true`,
    method: 'GET',
    withCredentials: true,
    headers: {
      'Content-Type': 'application/json; charset=utf-8'
    },
  })
    .then((response) => {
      if (response.status >= 200) {
        dispatch({ type: FETCH_PRODUCTS_LOADING, payload: false });
        dispatch({ type: FETCH_PRODUCTS_DATA, payload: response.data });
      } else {
        console.error(response.statusText);
        dispatch({ type: FETCH_PRODUCTS_ERROR, payload: true });
      }
    })
    .catch((err) => {
      console.error(err);
      dispatch({ type: FETCH_PRODUCTS_ERROR, payload: true });
    })
    .then(() => {
      const areas = getAreas(getState());
      const selectedArea = getSelectedArea(getState());

      if (!selectedArea) {
        // If there's no selected area or the productId present in the URL
        // isn't present in the results of the API, we set one by default
        dispatch(setArea(areas[0]));
      } else {
        // If we have a selected area it's because we've previously
        // restored the state and the productId still exits
        // In that case we want to make sure the state is valid. An easy
        // way is to set the area to it's current value, triggering each
        // of the selectors to do the same. If one of them has a wrong
        // value, they will be reset to a default one.
        dispatch(setArea(selectedArea));
      }
    });
};


/**
 * Set the current area
 * @param {{ label: string, value: string }} area Code of the area
 */
export const setArea = (area, resetDefault = false) => (dispatch, getState) => {
  const products = getValidProducts(getState());
  const product = products.find(p => p.domain.code === area.value);

  dispatch({ type: SET_PRODUCT_ID, payload: product.id });

  const scenarios = getScenarios(getState());
  const selectedScenario = getSelectedScenario(getState());

  const comparingScenarios = getComparingScenarios(getState());
  const selectedComparingScenario = getSelectedComparingScenario(getState());


  // We give:
  // - either a default value for the scenario
  // - either we need to reset the scenario because the option is not available anymore
  if (!selectedScenario || resetDefault) {
    let scenario = scenarios[0];

    if (
      defaultScenarios[product.productType.code]
      && defaultScenarios[product.productType.code].defaultVariable
    ) {
      const scenarioValue = defaultScenarios[product.productType.code].defaultVariable;
      scenario = scenarios.find(p => p.value === scenarioValue);
    }

    dispatch(setScenario(scenario));
  }
  dispatch(setPollutant(scenarios));

  // We give:
  // - either a default value for the scenario
  // - either we need to reset the scenario because the option is not available anymore
  if (!selectedComparingScenario || resetDefault) {
    let scenario = scenarios[1];

    if (
      defaultScenarios[product.productType.code]
      && defaultScenarios[product.productType.code].defaultVariable
    ) {
      const scenarioValue = defaultScenarios[product.productType.code].defaultVariable;
      scenario = comparingScenarios.find(p => p.value === scenarioValue);
    }
    dispatch(setComparingScenario(scenario));
  }

  const members = getMembers(getState());
  const selectedMember = getSelectedMember(getState());

  // We give:
  // - either a default value for the member
  // - either we need to reset the member because the option is not available anymore
  if ((!selectedMember || resetDefault) && members && members.length) {
    let member = members[0];

    if (
      defaultScenarios[product.productType.code]
      && defaultScenarios[product.productType.code].defaultEnsembleMember
    ) {
      const memberValue = defaultScenarios[product.productType.code].defaultEnsembleMember;
      member = members.find(m => m.value === memberValue);
    }

    dispatch(setMember(member));
  }

  const years = getYears(getState());
  const selectedYear = getSelectedYear(getState());

  // We give:
  // - either a default value for the year
  // - either we need to reset the year because the option is not available anymore
  if (!selectedYear) {
    dispatch(setYear(years[0]));
  }

  const months = getMonths(getState());
  const selectedMonth = getSelectedMonth(getState());

  // We give:
  // - either a default value for the month
  // - either we need to reset the month because the option is not available anymore
  if (!selectedMonth) {
    dispatch(setMonth(months[0]));
  }

  // We finally update the URL
  dispatch(updateUrl());
};

/**
 * Set the current scenario
 * @param {{ label: string, value: string }} scenario for product
 */
export const setScenario = scenario => (dispatch) => {
  dispatch({ type: SET_SCENARIO, payload: scenario });
  // We finally update the URL
  dispatch(updateUrl());
};

// /**
//  * Set the current pollutant
//  * @param {{ label: string, value: string }} area Code of the area
//  */
export const setPollutant = scenarios => (dispatch) => {
  if (defaultPreviewSimple) {
    const pollutant = {
      label: defaultPreviewSimple.pollutant,
      value: defaultPreviewSimple.pollutant,
      description: defaultPreviewSimple.description
    };
    dispatch({ type: SET_POLLUTANT, payload: pollutant });
  }
  else {
    const pollutantsValue = scenarios.map(scenario => scenario.value.split(['_'], 1));
    const pollutantsDescription = scenarios.map(scenario => scenario.description);


    const pollutant = pollutantsValue.reduce(
      (result, nextItem) => (
        result.includes(nextItem)
          ? result
          : result.concat(nextItem)
      ),
      []
    );

    const singleValuePollutant = pollutant.reduce(
      (result, nextItem) => (
        result.includes(nextItem)
          ? result
          : result.concat(nextItem)
      ),
      [].toString()
    );

    const formatPollutant = {
      label: singleValuePollutant,
      value: singleValuePollutant,
      description: pollutantsDescription
    };

    dispatch({ type: SET_POLLUTANT, payload: formatPollutant });
  }

  // We finally update the URL
  dispatch(updateUrl());
};

/**
 * Set the current scenario
 * @param {{ label: string, value: string }} area Code of the area
 */
export const setComparingScenario = scenario => (dispatch) => {
  dispatch({ type: SET_COMPARING_SCENARIO, payload: scenario });
  // We finally update the URL
  dispatch(updateUrl());
};

/**
 * Set the current member
 * @param {{ label: string, value: string }} member Member
 */
export const setMember = member => (dispatch) => {
  dispatch({ type: SET_MEMBER, payload: member.value });
  dispatch(updateUrl());
};

/**
 * Set the current language
 * @param {{ label: string, value: string }} language Language
 */
export const setLanguageF = language => (dispatch) => {
  dispatch({ type: SET_LANGUAGE, payload: language.value });
  dispatch(updateUrl());
};

export const updateDateTimezone = () => (dispatch, getState) => {
  const { date, timezone } = getState().atmoDemo.state;
  const dateTimezone = moment(date).utcOffset(timezone);

  dispatch({ type: UPDATE_DATE_TIMEZONE, payload: dateTimezone.format() });
};

export const updateComparingDateTimezone = () => (dispatch, getState) => {
  const { comparingDate, timezone } = getState().atmoDemo.state;
  const comparingDateTimezone = moment(comparingDate).utcOffset(timezone);

  dispatch({
    type: UPDATE_COMPARING_DATE_TIMEZONE,
    payload: comparingDateTimezone.format(),
  });
};

export const updateDate = () => (dispatch, getState) => {
  const { year, month, day, hour } = getState().atmoDemo.state;
  const date = moment.utc({ year, month, date: day, hour });
  dispatch({ type: UPDATE_DATE, payload: date.format() });
  dispatch(updateDateTimezone());
};

export const updateComparingDate = () => (dispatch, getState) => {
  const {
    comparingYear,
    comparingMonth,
    comparingDay,
    comparingHour,
  } = getState().atmoDemo.state;
  const comparingDate = moment.utc({
    year: comparingYear,
    month: comparingMonth,
    date: comparingDay,
    hour: comparingHour,
  });
  dispatch({ type: UPDATE_COMPARING_DATE, payload: comparingDate.format() });
  dispatch(updateComparingDateTimezone());
};
/**
 * Set the current timezone
 * @param {{ label: string, value: number }} timezone timezone
 */
export const setTimezone = timezone => (dispatch) => {
  dispatch({ type: SET_TIMEZONE, payload: timezone.offset });
  dispatch(updateDate());
  dispatch(updateComparingDate());
  dispatch(updateUrl());
};

export const setZone = timezone => (dispatch) => {
  dispatch({ type: SET_ZONE, payload: timezone.value });
};

/**
 * Set the current date
 * @param {{ label: string}}
 */
/**
 * Set the current year
 * @param {{ label: string, value: number }} year Year
 */
export const setYear = year => (dispatch, getState) => {
  dispatch({ type: SET_YEAR, payload: year.value });

  const months = getMonths(getState());
  const selectedMonth = getSelectedMonth(getState());

  // We give:
  // - either a default value for the month
  // - either we need to reset the month because the option is not available anymore
  if (!selectedMonth) {
    dispatch(setMonth(months[0]));
  }
  // We finally update the URL
  dispatch(updateUrl());
};
/**
 * Set the current month
 * @param {{ label: string, value: number }} month Month
 */
export const setMonth = month => (dispatch, getState) => {
  dispatch({ type: SET_MONTH, payload: month.value });

  const days = getDays(getState());
  const selectedDay = getSelectedDay(getState());

  // We give:
  // - either a default value for the day
  // - either we need to reset the day because the option is not available anymore
  if (!selectedDay) {
    dispatch(setDay(days[0]));
  }
  // Update date in store
  dispatch(updateDate());
  // We finally update the URL
  dispatch(updateUrl());
};

/**
 * Set the current day
 * @param {{ label: string, value: number }} day Day
 */
export const setDay = day => (dispatch, getState) => {
  dispatch({ type: SET_DAY, payload: day.value });

  const hours = getHours(getState());
  const selectedHour = getSelectedHour(getState());

  const defaultHour = hours.find(hour => hour.value === 8)

  // We give:
  // - either a default value for the hour
  // - either we need to reset the hour because the option is not available anymore
  if (!selectedHour) {
    defaultHour ? dispatch(setHour(defaultHour)) : dispatch(setHour(hours[0]));
  };

  const comparingDays = getComparingDays(getState());
  const selectedComparingDay = getSelectedComparingDay(getState());

  // We give:
  // - either a default value for the comparing day
  // - either we need to reset the comparing day because the option is not available anymore
  if (!selectedComparingDay && comparingDays.length) {
    dispatch(setComparingDay(comparingDays[0]));
  }
  // Update date in store
  dispatch(updateDate());

  // We finally update the URL
  dispatch(updateUrl());
};

/**
 * Set the current hour
 * @param {{ label: string, value: number }} hour Hour
 */
export const setHour = hour => (dispatch, getState) => {
  dispatch({ type: SET_HOUR, payload: hour.value });

  const comparingHours = getComparingHours(getState());
  const selectedComparingHour = getSelectedComparingHour(getState());

  // We give:
  // - either a default value for the comparing hour
  // - either we need to reset the comparing hour because the option is not available anymore
  if (!selectedComparingHour && comparingHours.length) {
    dispatch(setComparingHour(comparingHours[0]));
  }
  // Update date in store
  dispatch(updateDate());

  // We finally update the URL
  dispatch(updateUrl());
};

/**
 * Set the current comparing year
 * @param {{ label: string, value: number }} month Month
 */
export const setComparingYear = year => (dispatch, getState) => {
  dispatch({ type: SET_COMPARING_YEAR, payload: year && year.value });

  const comparingMonths = getComparingMonths(getState());
  const comparingMonth = getSelectedComparingMonth(getState());

  // We give:
  // - either a default value for the comparing day
  // - either we need to reset the comparing day because the option is not available anymore
  if (!comparingMonth && comparingMonths.length) {
    dispatch(setComparingMonth(comparingMonths[0]));
  }
  dispatch(updateComparingDate());

  dispatch(updateUrl());
};
/**
 * Set the current comparing month
 * @param {{ label: string, value: number }} month Month
 */
export const setComparingMonth = month => (dispatch, getState) => {
  dispatch({ type: SET_COMPARING_MONTH, payload: month && month.value });

  const comparingDays = getComparingDays(getState());
  const comparingDay = getSelectedComparingDay(getState());

  // We give:
  // - either a default value for the comparing day
  // - either we need to reset the comparing day because the option is not available anymore
  if (!comparingDay && comparingDays.length) {
    dispatch(setComparingDay(comparingDays[0]));
  }
  dispatch(updateComparingDate());

  dispatch(updateUrl());
};

/**
 * Set the current comparing day
 * @param {{ label: string, value: number }} day Day
 */
export const setComparingDay = day => (dispatch, getState) => {
  dispatch({ type: SET_COMPARING_DAY, payload: day && day.value });

  const comparingHours = getComparingHours(getState());
  const comparingHour = getSelectedComparingHour(getState());

  // We give:
  // - either a default value for the comparing hour
  // - either we need to reset the comparing hour because the option is not available anymore
  if (!comparingHour && comparingHours.length) {
    dispatch(setComparingHour(comparingHours[0]));
  }
  dispatch(updateComparingDate());

  dispatch(updateUrl());
};

/**
 * Set the current comparing hour
 * @param {{ label: string, value: number }} hour Hour
 */
export const setComparingHour = hour => (dispatch, getState) => {
  dispatch({ type: SET_COMPARING_HOUR, payload: hour && hour.value });

  const comparingHours = getComparingHours(getState());
  const selectedComparingHour = getSelectedComparingHour(getState());

  // We give:
  // - either a default value for the comparing hour
  // - either we need to reset the comparing hour because the option is not available anymore
  if (!selectedComparingHour && comparingHours.length) {
    dispatch(setComparingHour(comparingHours[0]));
  }
  dispatch(updateComparingDate());
  // We finally update the URL
  dispatch(updateUrl());
};

/**
 * Start the comparison by setting the comparing hour
 */
export const startComparing = () => (dispatch, getState) => {
  const comparingYears = getComparingYears(getState());
  dispatch(setComparingYear(comparingYears[0]));

  const comparingMonths = getComparingMonths(getState());
  if (!comparingMonths.length) return;

  dispatch(setComparingMonth(comparingMonths[0]));

  // We finally update the URL
  dispatch(updateUrl());
};

// /**
//  * Stop the comparison by resetting the comparing hour
//  */
// export const stopComparing = () => dispatch => {
//   dispatch(setComparingMonth(null));

//   // We finally update the URL
//   dispatch(updateUrl());
// };

/**
 * Update the URL according to the state of the demo
 */
export const updateUrl = () => (dispatch, getState) => {
  const { atmoDemo } = getState();
  const { state } = atmoDemo;
  const {
    productId,
    scenario,
    member,
    year,
    month,
    day,
    hour,
    comparingYear,
    comparingMonth,
    comparingDay,
    comparingHour,
    basemap,
    labels,
    language,
    viewport,
  } = state;

  const product = getSelectedProduct(getState());
  if (!product) return;

  let date = null;
  if (hour !== null) {
    date = new Date(product.tMax);
    date.setUTCFullYear(year);
    date.setUTCMonth(month);
    date.setUTCHours(hour);
    date.setUTCDate(day);
    date.setUTCMinutes(0);
    date.setUTCSeconds(0);
  }

  let comparingDate = null;
  if (comparingHour !== null) {
    comparingDate = new Date(product.tMax);
    comparingDate.setUTCFullYear(comparingYear);
    comparingDate.setUTCMonth(comparingMonth);
    comparingDate.setUTCDate(comparingDay);
    comparingDate.setUTCHours(comparingHour);
    comparingDate.setUTCMinutes(0);
    comparingDate.setUTCSeconds(0);
  }

  const params = {
    id: productId,
    scenario: JSON.stringify(scenario),
    member,
    date: date ? date.toISOString() : null,
    comparingDate: comparingDate ? comparingDate.toISOString() : null,
    basemap,
    labels,
    language,
    viewport: JSON.stringify(viewport),
  };

  const search = Object
    .keys(params)
    .reduce(
      (res, key) => (params[key] ? `${res}${key}=${params[key]}&` : res),
      '?',
    )
    .slice(0, -1);

  dispatch(replace({ search }));
};

/**
 * Set the state attribute of the store
 * @param {{ [key: string]: string|number }} newState New state
 */
export const setState = newState => (dispatch) => {
  const acceptedKeys = Object.keys(defaultState.state);
  if (newState.scenario) {
    newState.scenario = JSON.parse(decodeURIComponent(newState.scenario));
  }

  if (newState.viewport) {
    newState.viewport = JSON.parse(decodeURIComponent(newState.viewport));
  }
  dispatch({ type: SET_STATE, payload: pick(newState, acceptedKeys) });

  dispatch(updateUrl());
};

/**
 * Restore the state of the demo from the URL
 */
export const restoreState = () => (dispatch, getState) => {
  const { router } = getState();
  const { location } = router;
  const { search } = location;

  if (search.length < 2) return;

  // URL params deserialized
  const params = {};

  const keyValues = search.slice(1, search.length).split('&');
  for (let i = 0, j = keyValues.length; i < j; i++) {
    const keyValue = keyValues[i];
    const split = keyValue.split('=');

    // If we have a malformed key-value, we skip it
    if (split.length !== 2) continue;

    // eslint-disable-line no-continue
    const key = split[0];
    const value = split[1];

    if (key === 'id') {
      params.productId = value;
    } else if (key === 'date') {
      try {
        const date = new Date(value);
        params.year = date.getUTCFullYear();
        params.month = date.getUTCMonth();
        params.day = date.getUTCDate();
        params.hour = date.getUTCHours();
      } catch (err) {
        console.error('Unable to restore the date', err);
      }
    } else if (key === 'comparingDate') {
      try {
        const date = new Date(value);
        params.comparingYear = date.getUTCFullYear();
        params.comparingMonth = date.getUTCMonth();
        params.comparingDay = date.getUTCDate();
        params.comparingHour = date.getUTCHours();
      } catch (err) {
        console.error('Unable to restore the comparing date', err);
      }
    } else {
      params[key] = value;
    }
  }

  // If there's not productId, we don't restore the state because later
  // we use this attribute to determine whether we're restoring or not
  // in order to perform a check on the values
  if (params.productId) {
    dispatch(setState(params));
    dispatch(updateDate());
    dispatch(updateComparingDate());
  }
};

/**
 * Set the basemap
 * @param {string} basemap Basemap ID
 */
export const setBasemap = basemap => (dispatch) => {
  dispatch({ type: SET_BASEMAP, payload: basemap });

  dispatch(updateUrl());
};

/**
 * Set the labels
 * @param {string} labels labels ID
 */
export const setLabels = labels => (dispatch) => {
  dispatch({ type: SET_LABELS, payload: labels });

  dispatch(updateUrl());
};


export const setViewport = viewport => (dispatch) => {
  dispatch({ type: SET_VIEWPORT, payload: viewport });

  dispatch(updateUrl());
};



// WEBPACK FOOTER //
// ./src/components/atmo-demo/actions.js